import React, { useContext } from 'react';
import { AuthContext, IAuthContext } from 'src/state/providers/AuthProvider';
import {
  ACCESS_DENIED_PAGE_ROUTE,
  DEPLOYED_STAGE,
  NO_AUTH_STAGE,
} from 'src/constants/UrlConstants';
import { Navigate } from 'react-router';
import {
  fetchEmailDomain,
  validateAuthorizedEmail,
} from 'src/utilities/AuthParamHelper';
import { PROGRAM_ACCOUNTS, UI_CLIENTS } from 'src/constants/GlobalConstants';
import { logger } from 'src/logger';

export interface IProtectedRoute {
  allowedClients?: UI_CLIENTS[];
}

export function checkEmailDomainValidity(authContext: IAuthContext): boolean {
  const emailDomain = fetchEmailDomain(
    authContext.preferredAccountId,
    authContext.accountAttributes,
  );
  logger.info(`Fetched email domain: ${emailDomain}`);
  return emailDomain
    ? validateAuthorizedEmail(authContext.emailId!, emailDomain)
    : false;
}

/**
 * Check if the uiClient is allowed to access this page
 * @param currentUIClient current accessed page
 * @param validUiClients  list of valid UI clients.
 * @returns boolean
 */
export function checkValidClientAccess(
  currentUIClient: UI_CLIENTS,
  validUiClients?: UI_CLIENTS[],
): boolean {
  if (!validUiClients) {
    return true;
  }

  if (Array.isArray(validUiClients)) {
    return (
      validUiClients.filter((validClient) => validClient === currentUIClient)
        .length > 0
    );
  }
  return false;
}

/**
 * This HOC takes care of authenticating users based on his email domain.
 */
const ProtectedRoute: React.FC<IProtectedRoute> = ({
  allowedClients,
  children,
}) => {
  const authContext = useContext(AuthContext);
  /**
   * Check for valid UI Clients.
   */
  const isAllowlistedPage = checkValidClientAccess(
    authContext.clientId,
    allowedClients,
  );
  if (!isAllowlistedPage) {
    return <Navigate to={ACCESS_DENIED_PAGE_ROUTE} />;
  }

  /**
   * Disabling Auth for VICI and (amazon users in Beta and Gamma)
   */
  const authNotRequired =
    NO_AUTH_STAGE.filter((stage) => stage == DEPLOYED_STAGE).length > 0;
  logger.info(`Auth required Status is: ${authNotRequired}. Context:`, {
    authContext,
  });

  /**
   * All the access from VICI portal, Trusted Consumers and non prod account should not perform domain check.
   */
  if (
    authContext.clientId === UI_CLIENTS.VICI ||
    authContext.preferredAccountId === PROGRAM_ACCOUNTS.TRUSTED_CONSUMER ||
    authNotRequired
  ) {
    return <>{children}</>;
  }
  const isValidEmailDomain = checkEmailDomainValidity(authContext);
  if (!isValidEmailDomain) {
    return <Navigate to={ACCESS_DENIED_PAGE_ROUTE} />;
  }
  return <>{children}</>;
};

export default ProtectedRoute;
